import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fmtMT } from "@/lib/mountainTime";
import { reactProbe, reactProbes, errText } from "@/lib/fbLabApi";

const yn = (v) => (v === true ? "Yes" : v === false ? "No" : "Unknown");
const KIND = { page: "Bad Ass Horse Sales Page", personal: "Personal Facebook profile", unknown: "Unknown identity" };

const Row = ({ k, v, id }) => <div className="flex gap-2"><span className="text-stone-500 w-56 shrink-0">{k}</span><span data-testid={id} className="font-medium break-all">{v}</span></div>;

function Result({ j }) {
  const v = j.result?.diag?.verdict;
  if (!v) return <p className="text-xs text-stone-600" data-testid="lab-likecheck-status">{j.status === "failed" ? `Failed: ${j.error || j.result?.state}` : `Status: ${j.status}`}</p>;
  return (
    <div data-testid="lab-likecheck-result" className="text-xs space-y-0.5">
      <p className="font-semibold">{v.summary}</p>
      <Row k="1. Exact comment ID found" v={v.copies > 1 ? `${yn(v.comment_found)} · shown ${v.copies} times, ${v.copies_consistent ? "all identical" : "NOT identical"}` : yn(v.comment_found)} id="lab-lc-found" />
      <Row k="2. Text and author match exactly" v={`${yn(v.text_exact)} · author ${yn(v.author_ok)}`} id="lab-lc-text" />
      <Row k="3. Active identity" v={`${KIND[v.identity_kind] || "Unknown identity"}${v.identity_names_seen?.length ? ` (${v.identity_names_seen.join(" / ")})` : ""}`} id="lab-lc-identity" />
      <Row k="4. Page offered as an identity" v={yn(v.page_available)} id="lab-lc-page" />
      <Row k="5. Like button identified" v={`${yn(v.like_button_found)}${v.chosen_copy != null ? ` · copy ${v.chosen_copy + 1}${v.chosen_in_dialog ? " (post window)" : ""}` : ""}`} id="lab-lc-like" />
      <Row k="6. Already liked" v={yn(v.already_liked)} id="lab-lc-liked" />
      <Row k="7. Reacting identity determinable" v={`${yn(v.identity_determinable)} · ${v.identity_reason || ""}`} id="lab-lc-determinable" />
      <Row k="Safe to react automatically" v={v.safe_to_react ? "Yes" : `No${v.blockers?.length ? ` — ${v.blockers.join(", ")}` : ""}`} id="lab-lc-safe" />
      <p className="text-stone-500">Checked {fmtMT(j.updated_at)} · nothing was clicked.</p>
    </div>
  );
}

export function LikeCheck({ c }) {
  const [jobs, setJobs] = useState([]);
  const load = useCallback(() => { reactProbes(c.id).then(setJobs).catch(() => {}); }, [c.id]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    try { await reactProbe(c.id); toast.success("Like check queued. The extension runs it within a minute; nothing is clicked."); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <details className="text-[11px] border-t pt-1" data-testid={`lab-likecheck-${c.id}`}>
      <summary className="font-semibold cursor-pointer">Facebook Like check (read-only)</summary>
      <div className="space-y-2 pt-1">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`lab-likecheck-run-${c.id}`} onClick={run}>Run read-only check</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`lab-likecheck-refresh-${c.id}`} onClick={load}>Refresh</Button>
        </div>
        {jobs[0] && <Result j={jobs[0]} />}
      </div>
    </details>
  );
}
